import { Paper, Text } from '@mantine/core';
import { usePrevious } from '@mantine/hooks';
import React, { FC, useMemo } from 'react';

import { useDevice, useDeviceLastKnownState } from '@portals/api/organizations';
import { NewWidgetType } from '@portals/device-widgets';

import { getDeviceWidgetWrapperRenderer } from './device-widgets.constants';
import { WidgetProps } from './device-widgets.types';

interface WidgetWrapperProps {
  widget: NewWidgetType;
  deviceId: string;
  isDeviceOffline: boolean;
  modelId: string;
}

export function DeviceWidgetWrapper({
  widget,
  deviceId,
  isDeviceOffline,
  modelId,
}: WidgetWrapperProps) {
  const { config } = widget;

  const device = useDevice(deviceId, {
    staleTime: 5000,
    refetchInterval: 5000,
  });
  const Widget: FC<WidgetProps> = getDeviceWidgetWrapperRenderer(config.id);

  const previousDeviceFetchedData = usePrevious(device.data);

  // The request to fetch the last known state of the device is expensive
  // and should only be done when the device was offline at the time of the first request.
  // So, to explain the conditions:
  // => if the previous device data is `undefined` and the `device.data` is defined
  // => this is the point when the data was fetched for the first time
  // => check if the device was offline at that time, and if so, fetch the last known state.
  const shouldQueryLastKnownState =
    previousDeviceFetchedData === undefined && isDeviceOffline;

  const deviceLastKnownState = useDeviceLastKnownState(deviceId, {
    enabled: shouldQueryLastKnownState,
  });

  const adjustedState = useMemo(() => {
    if (!device.isFetched) return {};

    if (!isDeviceOffline) {
      return {
        ...(device.data?.details || {}),
        ...device.data.state,
      };
    }

    if (deviceLastKnownState.data) {
      return {
        ...(device.data?.details || {}),
        ...deviceLastKnownState.data.state,
      };
    }

    return {};
  }, [device.data, device.isFetched, deviceLastKnownState, isDeviceOffline]);

  if (device.isError || (!device.data && deviceLastKnownState.isError)) {
    return (
      <Paper p="md" radius="lg" h="100%">
        <Text weight={600} size="md" mb="xs">
          Oops...
        </Text>

        <Text>An error occurred while trying to fetch widget data</Text>
      </Paper>
    );
  }

  return (
    <Widget
      widget={widget}
      deviceState={adjustedState}
      deviceId={deviceId}
      modelId={modelId}
      isDeviceOffline={isDeviceOffline}
      lastUpdateTimestamp={device?.data?.last_seen}
    />
  );
}
